import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { AIVoiceFilter } from 'js/src/client';
import {
    fetchAIVoicePublicFilters,
    getAIVoiceAccentFilters,
    getAIVoiceAgeFilters,
    getAIVoiceFiltersRequestIsPending,
    getAIVoiceFiltersRequestIsUninitialized,
    getAIVoiceGenderFilters,
    getAIVoiceLanguageFilters,
    getAIVoiceQualityFilters,
    getAIVoiceStyleFilters,
    getAIVoiceToneFilters,
} from 'js/src/data/ai-voices-and-avatars';
import { useAppDispatch } from 'js/src/libs/RootState';

export type AIVoiceFilterOption = {
    value: string;
    display: string;
};

const transformFiltersToFilterOptions = (filters: AIVoiceFilter[]): AIVoiceFilterOption[] => {
    return filters.map((filter) => ({
        value: filter.value,
        display: filter.display,
    }));
};

const useAIVoiceFilters = () => {
    const dispatch = useAppDispatch();

    const triggerFetch = useCallback(() => {
        dispatch(fetchAIVoicePublicFilters());
    }, [dispatch]);

    const isUninitialized = useSelector(getAIVoiceFiltersRequestIsUninitialized);
    const isFetching = useSelector(getAIVoiceFiltersRequestIsPending);

    const accentFilters = useSelector(getAIVoiceAccentFilters);
    const toneFilters = useSelector(getAIVoiceToneFilters);
    const styleFilters = useSelector(getAIVoiceStyleFilters);
    const qualityFilters = useSelector(getAIVoiceQualityFilters);
    const genderFilters = useSelector(getAIVoiceGenderFilters);
    const ageFilters = useSelector(getAIVoiceAgeFilters);
    const languageFilters = useSelector(getAIVoiceLanguageFilters);

    const accentFilterOptions = useMemo(
        () => transformFiltersToFilterOptions(accentFilters),
        [accentFilters],
    );

    const toneFilterOptions = useMemo(
        () => transformFiltersToFilterOptions(toneFilters),
        [toneFilters],
    );

    const styleFilterOptions = useMemo(
        () => transformFiltersToFilterOptions(styleFilters),
        [styleFilters],
    );

    const qualityFilterOptions = useMemo(
        () => transformFiltersToFilterOptions(qualityFilters),
        [qualityFilters],
    );

    const genderFilterOptions = useMemo(
        () => transformFiltersToFilterOptions(genderFilters),
        [genderFilters],
    );

    const ageFilterOptions = useMemo(
        () => transformFiltersToFilterOptions(ageFilters),
        [ageFilters],
    );

    const languageFilterOptions = useMemo(
        () => transformFiltersToFilterOptions(languageFilters),
        [languageFilters],
    );

    return {
        triggerFetch,
        isUninitialized,
        isFetching,
        accentFilterOptions,
        toneFilterOptions,
        styleFilterOptions,
        qualityFilterOptions,
        genderFilterOptions,
        ageFilterOptions,
        languageFilterOptions,
    };
};

export default useAIVoiceFilters;
